import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Settings, Package, TestTube, Globe } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export const StockTestModeManager = () => {
  const [loading, setLoading] = useState(false);
  const [globalTestMode, setGlobalTestMode] = useState(false);
  const [systemTestMode, setSystemTestMode] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('key, value')
        .in('key', ['stock_test_mode_global', 'stock_test_mode']);

      if (error) throw error;

      const globalSetting = data?.find(s => s.key === 'stock_test_mode_global');
      const systemSetting = data?.find(s => s.key === 'stock_test_mode');

      // Les valeurs sont stockées en string "true"/"false" dans site_settings
      setGlobalTestMode(globalSetting?.value === 'true' || globalSetting?.value === true);
      setSystemTestMode(systemSetting?.value === 'true' || systemSetting?.value === true);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les paramètres",
        variant: "destructive"
      });
    }
  };

  const updateSetting = async (key: string, value: boolean) => {
    setLoading(true);
    try {
      // Stocker comme string pour cohérence avec le reste de site_settings
      const { error } = await supabase
        .from('site_settings')
        .upsert({ 
          key, 
          value: value.toString(),
          updated_at: new Date().toISOString()
        }, { onConflict: 'key' });

      if (error) throw error;

      // Mettre à jour l'état local immédiatement
      if (key === 'stock_test_mode_global') {
        setGlobalTestMode(value);
      } else if (key === 'stock_test_mode') {
        setSystemTestMode(value);
      }

      toast({
        title: "Succès",
        description: value ? "Mode test activé" : "Mode production activé",
      });

    } catch (error) {
      console.error('Error updating setting:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le paramètre",
        variant: "destructive"
      });
      // Re-fetch en cas d'erreur pour synchro
      fetchSettings();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <TestTube className="h-5 w-5" />
          <CardTitle>Mode Test du Stock</CardTitle>
        </div>
        <CardDescription>
          Configuration du mode test pour la gestion du stock. En mode test, le stock est virtuel et illimité.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Mode global pour tous les vendeurs */}
        <div className="flex items-center justify-between space-x-2 p-4 border rounded-lg">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="global-test-mode" className="text-base font-semibold">
                Mode Test Global
              </Label>
              {globalTestMode && (
                <Badge variant="secondary" className="ml-2">
                  <TestTube className="h-3 w-3 mr-1" />
                  Actif
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Activer le mode test pour tous les vendeurs (prioritaire sur les configurations individuelles)
            </p>
          </div>
          <Switch
            id="global-test-mode"
            checked={globalTestMode}
            onCheckedChange={(checked) => updateSetting('stock_test_mode_global', checked)}
            disabled={loading}
          />
        </div>

        {/* Mode système (fallback) */}
        <div className="flex items-center justify-between space-x-2 p-4 border rounded-lg">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="system-test-mode" className="text-base font-semibold">
                Mode Test Système
              </Label>
              {systemTestMode && !globalTestMode && (
                <Badge variant="secondary" className="ml-2">
                  <TestTube className="h-3 w-3 mr-1" />
                  Actif
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Mode test par défaut si aucun mode global ou individuel n'est configuré
            </p>
          </div>
          <Switch
            id="system-test-mode"
            checked={systemTestMode}
            onCheckedChange={(checked) => updateSetting('stock_test_mode', checked)}
            disabled={loading}
          />
        </div>

        {/* Informations sur le mode actif */}
        <div className="bg-muted p-4 rounded-lg space-y-2">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            <span className="font-semibold">Mode actuel :</span>
          </div>
          <div className="text-sm space-y-1">
            {globalTestMode ? (
              <div className="flex items-center gap-2 text-amber-600">
                <TestTube className="h-4 w-4" />
                <span>Mode TEST Global - Stock virtuel illimité pour tous les vendeurs</span>
              </div>
            ) : systemTestMode ? (
              <div className="flex items-center gap-2 text-amber-600">
                <TestTube className="h-4 w-4" />
                <span>Mode TEST Système - Stock virtuel illimité par défaut</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-green-600">
                <Package className="h-4 w-4" />
                <span>Mode PRODUCTION - Validation stricte du stock réel</span>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Note : Les vendeurs peuvent avoir leur propre configuration dans la section Gestion des Vendeurs.
              Les paramètres individuels sont prioritaires sur le mode système, mais le mode global est toujours prioritaire.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
